import { ReactNode, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import DocumentationIconButton from "components/DocumentationIconButton";
import NotFoundPage from "components/error/NotFoundPage";
import MetaInfoList from "components/MetaInfoList";
import DesktopView from "components/Mobile/DesktopView";
import MobileView from "components/Mobile/MobileView";
import SearchInput from "components/SearchInput";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import BadgeNext from "ds/components/BadgeNext";
import useTitle from "hooks/useTitle";

import { getIntegrationById, isValidIntegrationId } from "../../registry";
import { IntegrationsOutletContext } from "../../types";

const IntegrationGroupLayout = () => {
  const { integrationGroup, integrationId } = useParams<{
    integrationGroup: string;
    integrationId?: string;
  }>();
  const [title, setTitle] = useState<string>("Integrations");

  const [layoutActions, setLayoutActions] = useState<ReactNode>(null);
  const [layoutMeta, setLayoutMeta] = useState<ReactNode>(null);

  const [integration, showNewBadge, showSearch] = useMemo(() => {
    if (isValidIntegrationId(integrationGroup)) {
      const integration = getIntegrationById(integrationGroup);
      const showNewBadge = integration?.isNew && !integrationId;
      const showSearch = integration?.hasViewScreen && integration?.hasSearch && !integrationId;
      return [integration, showNewBadge, showSearch];
    }
    return [];
  }, [integrationGroup, integrationId]);

  const memoizedContext: IntegrationsOutletContext | null = useMemo(() => {
    if (!integration?.hasViewScreen) return null;

    return {
      integration,
      setLayoutActions,
      setLayoutMeta,
      setLayoutHeading: setTitle,
    };
  }, [integration]);

  useTitle(`Integrations · ${title}`);

  if (!integration?.hasViewScreen) {
    return <NotFoundPage />;
  }

  return (
    <>
      <ViewHeader firstLevel>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{title}</ViewHeaderTitle>
            {showNewBadge && <BadgeNext variant="blue" type="strong" text="New" />}
          </ViewHeaderWrapper>
          <ViewHeaderWrapper direction="row" justify="end">
            {showSearch && (
              <DesktopView>
                <SearchInput
                  filtersOrderSettingsKey="integrations"
                  placeholder="Search integrations..."
                />
              </DesktopView>
            )}
            {integration.docsLink && (
              <DocumentationIconButton
                tooltipText={`View documentation for ${title}`}
                href={integration.docsLink}
                variant="secondary"
              />
            )}
            {layoutActions}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
        {layoutMeta && (
          <ViewHeaderScrollCollapse>
            <MetaInfoList>{layoutMeta}</MetaInfoList>
          </ViewHeaderScrollCollapse>
        )}
        {showSearch && (
          <MobileView>
            <SearchInput
              filtersOrderSettingsKey="integrations"
              placeholder="Search integrations..."
            />
          </MobileView>
        )}
      </ViewHeader>
      <Outlet context={memoizedContext} />
    </>
  );
};

export default IntegrationGroupLayout;
